import { cn } from '@/lib/utils.ts'
import { ArrowLeft } from 'lucide-react'
import { MobileMenuStateT } from '@/store/useMenuStore.ts'
import { useNavigate } from 'react-router-dom'
import NavigationMobileCountBtn from '@/components/common/menu/navOverlays/NavigationMobileCountBtn.tsx'
import NavStagesPopover from '@/components/common/menu/navOverlays/navStagesOverlay/NavStagesPopover.tsx'
import { useTopbarContextStore } from '@/store/useTopbarContextStore.ts'

type PropsT = {
  menuState: MobileMenuStateT
  className?: string
}

export default function NavigationMobileHeader({ menuState, className }: PropsT) {
  const navigate = useNavigate()

  // Stack-driven back behavior. Just a bare arrow — no label, no current
  // screen title. Page content (h1, breadcrumb) already names the screen;
  // duplicating it in the topbar is redundant and confusing when the two
  // disagree during async title resolution.
  const stack = useTopbarContextStore(s => s.stack)
  const current = stack[stack.length - 1]
  const previous = stack.length >= 2 ? stack[stack.length - 2] : null

  // Back target: prefer real history; else the current screen's declared IA
  // fallback (deep-link case); else no back button.
  const backTarget = previous
    ? { kind: 'history' as const }
    : current?.fallbackBackTo
      ? { kind: 'fallback' as const, to: current.fallbackBackTo }
      : null

  const handleBack = () => {
    if (backTarget?.kind === 'history') {
      navigate(-1)
    } else if (backTarget?.kind === 'fallback') {
      navigate(backTarget.to)
    }
  }

  // Render: distinct back button — rounded border pill so it's visually
  // separate from the adjacent stage switcher and reads as a primary
  // control rather than a passive icon. No label, the arrow is enough.
  const leftContextual = backTarget ? (
    <button
      type="button"
      onClick={handleBack}
      aria-label="back"
      className={`flex size-8 shrink-0 items-center justify-center rounded-lg border bg-background text-foreground shadow-sm active:scale-95`}
    >
      <ArrowLeft className={`size-3.5`} />
    </button>
  ) : null

  return (
    <header
      className={cn(
        // `px-3 gap-3` gives the back button real breathing room on both
        // sides; without it the arrow button sat flush against the viewport
        // edge on the left and immediately against the stage switcher on
        // the right, reading as a single cramped strip rather than distinct
        // controls. Vertical padding is applied together with the safe-area
        // inset below (otherwise Tailwind's `pt-[var(--safe-area-inset-top)]`
        // overrides a plain `py-2` and collapses top padding to 0 on
        // non-notched devices like a desktop browser).
        `flex items-center gap-2 px-3 pb-1.5`,
        className,
        // Topbar sits below the banner stack. Primary navigation moved to
        // the bottom tab bar; this header keeps only the contextual back
        // arrow plus any queue/status indicators. Top padding adds the
        // safe-area inset (iOS notch) on top of a baseline 0.375rem so the
        // back button never hugs the top edge while keeping the bar tight.
        menuState === 'closedMobile' &&
          `fixed left-0 right-0 top-[var(--top-banners-offset,0px)] z-[100] border-b bg-background pt-[calc(0.375rem+var(--safe-area-inset-top,0px))]`,
      )}
    >
      {leftContextual}
      {/* Stage switcher lives in the topbar — it's a per-session context
          switch, not a primary navigation action. Stretched to fill all
          available space between the back button and the count button so
          long project/stage names stay readable. Label text is right-aligned
          (triggerClassName → `justify-end`) so the project name hugs the
          chevron on the right edge and reads naturally from right-to-left
          when scanning after tapping back. `px-0` overrides the drawer
          gutter baked into NavStagesPopover. */}
      <NavStagesPopover
        // `w-auto` overrides the switcher's default `w-full` (sidebar uses
        // full width; topbar wants content-sized). `ml-auto` then pushes
        // the switcher + count button to the right edge. `min-w-0` keeps
        // the label truncatable on narrow screens with long project names.
        // `py-0` strips the sidebar wrapper's vertical padding so the
        // switcher height is driven entirely by the trigger button itself.
        className={`ml-auto w-auto min-w-0 px-0 py-0`}
        // `h-8` matches the back button on mobile (was h-9 from Button's
        // size="sm"). `w-auto justify-end` keeps the topbar layout the
        // same as before.
        triggerClassName={`h-8 w-auto justify-end`}
        popoverAlign={`end`}
      />
      <NavigationMobileCountBtn />
    </header>
  )
}
